import { Link } from "wouter";
import { ORCID_URL, WIKIDATA_URL, NPI_REGISTRY_URL } from "@/lib/seo";

/**
 * Visible reviewer panel (network standard section 4). Carries Person
 * microdata pointing at the canonical @id so the visible block and the
 * JSON-LD describe the same person.
 */
export function ReviewerPanel({
  reviewedDate,
  reviewedLabel,
  className = "",
}: {
  reviewedDate: string;
  reviewedLabel: string;
  className?: string;
}) {
  return (
    <aside
      aria-label="Medical reviewer"
      className={`reviewer-panel flex gap-4 items-start border border-border bg-background/70 p-4 md:p-5 ${className}`}
      itemScope
      itemType="https://schema.org/Person"
      itemID="https://shariqrefai.com/#person"
    >
      <span
        aria-hidden="true"
        className="shrink-0 inline-flex h-11 w-11 items-center justify-center rounded-full bg-primary text-primary-foreground font-serif text-base"
      >
        SR
      </span>
      <div className="text-sm leading-relaxed text-secondary font-light">
        <p className="text-primary font-medium">
          Reviewed by{" "}
          <Link href="/about" className="hover:text-accent transition-colors">
            <span itemProp="name">Shariq Refai, MD, MBA, FAPA</span>
          </Link>
        </p>
        <p>
          <span itemProp="jobTitle">Board-certified psychiatrist</span> and founder of The
          Shrink Network. Last reviewed <time dateTime={reviewedDate}>{reviewedLabel}</time>.
          Every essay is reviewed at least once a year.
        </p>
        <p className="mt-2 text-xs">
          <span className="text-muted-foreground">Verify independently: </span>
          <a itemProp="sameAs" href={ORCID_URL} target="_blank" rel="noopener noreferrer" className="text-accent hover:text-primary">
            ORCID
          </a>
          <span aria-hidden="true"> · </span>
          <a itemProp="sameAs" href={WIKIDATA_URL} target="_blank" rel="noopener noreferrer" className="text-accent hover:text-primary">
            Wikidata
          </a>
          <span aria-hidden="true"> · </span>
          <a itemProp="sameAs" href={NPI_REGISTRY_URL} target="_blank" rel="noopener noreferrer" className="text-accent hover:text-primary">
            NPI Registry
          </a>
          <span aria-hidden="true"> · </span>
          <Link href="/verified" className="text-accent hover:text-primary">
            All verification links
          </Link>
        </p>
      </div>
    </aside>
  );
}

/**
 * "How to cite this page" block with Short, APA, and MLA formats. Each
 * format is select-all on click so readers can copy it in one move.
 */
export function CiteThisPage({
  title,
  url,
  year,
  className = "",
}: {
  title: string;
  url: string;
  year: number;
  className?: string;
}) {
  const bare = url.replace(/^https?:\/\//, "");
  const formats = [
    { label: "Short", text: `"${title}," Shariq Refai, MD, MBA, FAPA, shariqrefai.com, ${url}.` },
    { label: "APA", text: `Refai, S. (${year}). ${title}. Shariq Refai, MD, MBA. ${url}` },
    { label: "MLA", text: `Refai, Shariq. "${title}." Shariq Refai, MD, MBA, ${year}, ${bare}.` },
  ];
  return (
    <section aria-labelledby="cite-heading" className={`cite-this-page ${className}`}>
      <h2 id="cite-heading" className="text-xs font-bold tracking-widest uppercase text-muted-foreground mb-4">
        How to cite this page
      </h2>
      <dl className="space-y-3">
        {formats.map((f) => (
          <div key={f.label}>
            <dt className="text-xs uppercase tracking-wider text-muted-foreground">{f.label}</dt>
            <dd className="mt-1 select-all cursor-text text-sm text-secondary font-light bg-muted/40 border border-border px-3 py-2 break-words">
              {f.text}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

/** Evidence label linked to the site's evidence standards page. */
export function EvidenceLabel({
  level = "educational",
  className = "",
}: {
  level?: "strong" | "emerging" | "educational";
  className?: string;
}) {
  const text = {
    strong: "Strong evidence. The central claims rest on consistent findings across well-designed studies.",
    emerging: "Emerging evidence. Some claims rest on early or mixed research, and we say so where it matters.",
    educational:
      "Educational perspective. A psychiatrist's explanation grounded in clinical experience and the cited research, not a systematic review.",
  }[level];
  return (
    <p className={`evidence-label text-xs text-muted-foreground font-light ${className}`}>
      <span className="font-medium text-primary">Evidence label: </span>
      {text}{" "}
      <Link href="/evidence" className="text-accent hover:text-primary">
        How we label evidence
      </Link>
    </p>
  );
}
